// CLA-81 review (A1/A3/A16): the user's *intended* mic/cam enabled state for
// the active video call, held outside any React root so it survives root
// teardown -- in-class Turbo navigation, the mobile-overlay reparent -- and
// is shared between the React ControlBar and the mobile inline toggle
// (video_call_controller.js#toggleMic/toggleCam), so both surfaces agree.
//
// This is INTENT, not reality -- but VideoPanel.jsx's mic-off badge reads
// BOTH: intent off means the mic was never published at all (nothing to
// mute), so publication state alone can't detect it (A1 review round 2).
// Badge = intent-off OR muted-publication; buttons/props = intent alone.
//
// Scoped to the CALL, not the page: nothing resets this on an in-class
// remount/reparent (that would defeat A3/A16), but a genuine call end must
// call `resetMediaIntent()` -- see its own docstring for which call sites
// qualify.

let micEnabled = true;
let camEnabled = true;
const listeners = new Set();

function notify() {
  listeners.forEach((listener) => listener({ micEnabled, camEnabled }));
}

export function getMicIntent() {
  return micEnabled;
}

export function getCamIntent() {
  return camEnabled;
}

export function setMicIntent(value) {
  if (micEnabled === value) return;
  micEnabled = value;
  notify();
}

export function setCamIntent(value) {
  if (camEnabled === value) return;
  camEnabled = value;
  notify();
}

// Returns an unsubscribe function.
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// CLA-81 review blocker 2: back to defaults on a genuine call end (the
// `handleCallEnded()` path and the `call_ended` broadcast branch in
// video_call_controller.js), NOT on an in-class remount, the mobile-overlay
// reparent, or any other mid-call teardown -- those must keep the intent
// alive across the React root they tear down, which is the entire point of
// this module. Without this reset, muting once would silently carry over
// to every later call for the rest of the browser session (Turbo never
// re-evaluates this module).
export function resetMediaIntent() {
  const changed = micEnabled !== true || camEnabled !== true;
  micEnabled = true;
  camEnabled = true;
  if (changed) notify();
}
